import './signUpPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import {
  EMAIL_ADDRESS_PATTERN,
  PERSON_NAME_PATTERN,
  PHONE_NUMBER_PATTERN,
} from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import trim from 'lodash/trim';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import circleCheckmark from '../../assets/circle-checkmark.svg';
import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Checkbox } from '../../components/Checkbox/Checkbox';
import { Header } from '../../components/Header/Header';
import { Input } from '../../components/Input/Input';
import { LanderImage } from '../../components/LanderImage/LanderImage';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import useApiStore from '../../store/apiStore/useApiStore';
import { smsOptInCheckboxVariantDefault } from '../../store/featureStore/constants/variantDefaults';
import { FeatureFlag } from '../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../store/featureStore/hooks/useFeatureFlag';
import { useFeatureVariant } from '../../store/featureStore/hooks/useFeatureVariant';
import useFeatureStore from '../../store/featureStore/useFeatureStore';
import useLeadChannelStore from '../../store/leadChannelStore/useLeadChannelStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../store/routeStore/hooks/useNavigateWithCallback';
import useSignUpStore from '../../store/signUpStore/useSignUpStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { isLoading } = useApiStore();

  const { t } = useTranslation();

  const { sendSignUpVariantAssignedAnalytics } = useAnalyticStore();

  const {
    computed: { brand },
  } = useLeadChannelStore();

  const { deliverPin, deliverPinFailedMessage } = usePinStore();

  const {
    deriveDeliverPinParams,
    clearUserSuccessState,
    setSmsOptIn,
    userExists,
  } = useUserStore();

  const {
    computed: {
      reviewsAsset,
      reviewsUrl,
      signUpTitleText,
      signUpSubmitButtonText,
      signUpSubtitleFirstLineText,
      signUpSubtitleSecondLineText,
      signUpSubtitleThirdLineText,
    },
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
  } = useSignUpStore();

  const { isReady } = useFeatureStore();

  const smsOptInCheckboxVariant = useFeatureVariant(
    FeatureFlag.ENABLE_SMS_OPT_IN_CHECKBOX,
    smsOptInCheckboxVariantDefault,
  );

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: firstName,
      lastNameValue: lastName,
      primaryEmailAddressValue: primaryEmailAddress,
      primaryPhoneNumberValue: toPhoneString(primaryPhoneNumber),
      smsOptInValue: false,
    },
    delayError: 200,
    shouldFocusError: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = form;

  const isRefandiSignUpCopyEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_REFANDI_SIGN_UP_COPY,
  );

  const firstNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: PERSON_NAME_PATTERN,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: EMAIL_ADDRESS_PATTERN,
      message: t('validation.emailAddress.invalid'),
    },
  };

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
      smsOptInValue,
    } = values;

    await userExists(primaryPhoneNumberValue, primaryEmailAddressValue);

    const { userExistsException } = useUserStore.getState();

    setSmsOptIn(smsOptInValue);

    useSignUpStore.setState({
      firstName: firstNameValue,
      lastName: lastNameValue,
      primaryPhoneNumber: primaryPhoneNumberValue,
      primaryEmailAddress: primaryEmailAddressValue,
    });

    const deliverPinParams = deriveDeliverPinParams(
      userExistsException,
      DeliverPinLocation.SIGN_IN,
      primaryPhoneNumberValue,
      primaryEmailAddressValue,
    );

    await deliverPin(deliverPinParams);

    const {
      computed: { isAccountOwnershipUnverified },
      deliverPinSuccess,
    } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigateWithCallback('/sign-up/pin-verify').then((): void => {
        clearUserSuccessState();
      });
    }

    // CP-6949: the email belongs to an existing account and the phone is not
    // on it. Verifying a new phone does not prove the account is theirs, so
    // explain the options instead of sending a PIN. Plain navigate: there is
    // no callback, and navigateWithCallback only navigates inside .then()
    if (isAccountOwnershipUnverified) {
      navigate('/sign-up/returning-user');
    }
  };

  useEffect((): void => {
    if (!isReady) {
      return;
    }

    // Fire analytics event on page land signaling opt-in variant.
    sendSignUpVariantAssignedAnalytics({
      experiment_name: 'sms_opt_in_checkbox',
      variant: smsOptInCheckboxVariant,
    });
  }, [isReady, smsOptInCheckboxVariant]);

  // Because this page depends on the SMS opt-in variant to determine checkbox visibility,
  // wait until feature flags have loaded before rendering to avoid a flash of incorrect content.
  if (!isReady) {
    return null;
  }

  return (
    <div className="sign-up-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            trailing={
              <Link
                data-testid={locator.pages.signUp.header.signInLink}
                to="/sign-in"
              >
                {t('SignUpPage.signin')}
              </Link>
            }
            showLogo
            splitLayout
          />

          <main>
            <LanderImage
              alt={t('imageAlts.heroReference')}
              defaultImage={heroReferenceMessage}
            />
            <form
              data-testid={locator.pages.signUp.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signUp.form.h1}>
                  {signUpTitleText}
                </h1>

                {isRefandiSignUpCopyEnabled ? (
                  <h2 data-testid={locator.pages.signUp.form.h2}>
                    {t('SignUpPage.refandiSubtitle')}
                  </h2>
                ) : (
                  <ul>
                    <li>
                      <img alt="" src={circleCheckmark} />
                      <span>{signUpSubtitleFirstLineText}</span>
                    </li>
                    <li>
                      <img alt="" src={circleCheckmark} />
                      <span>{signUpSubtitleSecondLineText}</span>
                    </li>
                    <li>
                      <img alt="" src={circleCheckmark} />
                      <span>{signUpSubtitleThirdLineText}</span>
                    </li>
                  </ul>
                )}

                {deliverPinFailedMessage && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.signUp.alert.error}
                      severity="error"
                    >
                      {deliverPinFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Input
                        {...register('firstNameValue', firstNameValidation)}
                        data-testid={locator.pages.signUp.form.firstNameInput}
                        error={errors.firstNameValue}
                        errorTestId={
                          locator.pages.signUp.form.firstNameInputError
                        }
                        label={t('SignUpPage.firstName')}
                      />
                    </div>

                    <div className="input-half">
                      <Input
                        {...register('lastNameValue', lastNameValidation)}
                        data-testid={locator.pages.signUp.form.lastNameInput}
                        error={errors.lastNameValue}
                        errorTestId={
                          locator.pages.signUp.form.lastNameInputError
                        }
                        label={t('SignUpPage.lastName')}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <NumberInput
                      {...register(
                        'primaryPhoneNumberValue',
                        phoneNumberValidation,
                      )}
                      autoComplete="tel-national"
                      data-testid={locator.pages.signUp.form.phoneInput}
                      error={errors.primaryPhoneNumberValue}
                      errorTestId={locator.pages.signUp.form.phoneInputError}
                      label={t('SignInPage.phoneNumber')}
                      phone
                    />
                  </div>
                  <div>
                    <Input
                      {...register(
                        'primaryEmailAddressValue',
                        emailAddressValidation,
                      )}
                      data-testid={locator.pages.signUp.form.emailInput}
                      error={errors.primaryEmailAddressValue}
                      errorTestId={locator.pages.signUp.form.emailInputError}
                      label={t('SignUpPage.email')}
                    />
                  </div>

                  {smsOptInCheckboxVariant !==
                    smsOptInCheckboxVariantDefault && (
                    <div style={{ marginTop: '1rem' }}>
                      <Checkbox
                        {...register('smsOptInValue')}
                        checked={watch('smsOptInValue', false)}
                        label={
                          <Trans
                            i18nKey={'SignUpPage.smsOptInAcknowledgement'}
                            values={{ brand }}
                          />
                        }
                        testId={locator.pages.signUp.form.smsOptInCheckbox}
                        variant="sm"
                      />
                    </div>
                  )}
                </div>

                <Button
                  data-testid={locator.pages.signUp.form.submitButton}
                  loader={isLoading}
                  loaderPosition="start"
                  fullWidth
                  submit
                >
                  {signUpSubmitButtonText}
                </Button>

                <p className="form-terms-privacy">
                  <Trans
                    components={{
                      terms: (
                        <a
                          data-testid={locator.pages.signUp.form.termsLink}
                          href="https://www.thesavingsgroup.com/terms-of-use/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                      privacy: (
                        <a
                          data-testid={locator.pages.signUp.form.privacyLink}
                          href="https://www.thesavingsgroup.com/privacy-policy/"
                          rel="noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    i18nKey={'SignUpPage.termsPrivacy'}
                  />
                </p>
                <a
                  className="reviews-link"
                  data-testid={locator.pages.signUp.form.reviewsLink}
                  href={reviewsUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  <img alt={t('imageAlts.reviews')} src={reviewsAsset} />
                </a>
              </div>
              <footer data-testid={locator.pages.signUp.footer.root}>
                <span>
                  {t('copyright', { year: new Date().getFullYear() })}
                </span>
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignUpPage';
